/**
 * Admin Dashboard Page
 *
 * Overview metrics, filter strip, and split list / detail workspace.
 */

import { useMemo, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowUpRight,
  Bell,
  RefreshCw,
  Settings,
  ShieldCheck,
  UserPlus,
  Users,
} from 'lucide-react'
import { useGetDashboardDataQuery } from '../api/dashboardApi'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { AppCard } from '@/features/common/components/AppCard'
import { PillTabs } from '@/features/common/components/PillTabs'
import { FilterBar } from '@/features/common/components/FilterBar'
import { Button } from '@/features/common/components/Button'

type DashTab = 'overview' | 'activity'

interface OverviewItem {
  id: string
  title: string
  subtitle: string
  amount: string
  badge?: string
  initials: string
  accent: string
}

function MiniMetricBar({ value }: { value: number }) {
  const pct = Math.min(100, Math.round(value * 100))
  return (
    <div className="mt-3 h-2 overflow-hidden rounded-pill bg-white/50">
      <div
        className="h-full rounded-pill bg-brand/80 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

export function DashboardPage() {
  const { data, isLoading, error, refetch, isFetching } = useGetDashboardDataQuery()
  const [tab, setTab] = useState<DashTab>('overview')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const stats = data?.stats ?? { registeredUsers: 0, usersRegisteredToday: 0 }

  const overviewItems: OverviewItem[] = useMemo(
    () => [
      {
        id: 'registered',
        title: 'Registered users',
        subtitle: 'All-time total',
        amount: stats.registeredUsers.toLocaleString(),
        initials: 'RU',
        accent: 'bg-primary-100 text-primary-700',
      },
      {
        id: 'today',
        title: 'New today',
        subtitle: 'Sign-ups in the last 24h',
        amount: stats.usersRegisteredToday.toLocaleString(),
        badge: 'Live',
        initials: 'NT',
        accent: 'bg-emerald-100 text-emerald-700',
      },
      {
        id: 'health',
        title: 'Platform status',
        subtitle: 'Core services',
        amount: 'Operational',
        initials: 'OK',
        accent: 'bg-amber-100 text-amber-800',
      },
    ],
    [stats.registeredUsers, stats.usersRegisteredToday]
  )

  useEffect(() => {
    const first = overviewItems[0]
    if (first && selectedId == null) {
      setSelectedId(first.id)
    }
  }, [overviewItems, selectedId])

  const selected =
    overviewItems.find(i => i.id === selectedId) ?? overviewItems[0] ?? null

  const todayRatio =
    stats.registeredUsers > 0
      ? Math.min(1, stats.usersRegisteredToday / stats.registeredUsers)
      : stats.usersRegisteredToday > 0
        ? 1
        : 0.12

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return overviewItems
    return overviewItems.filter(
      i =>
        i.title.toLowerCase().includes(q) ||
        i.subtitle.toLowerCase().includes(q) ||
        i.amount.toLowerCase().includes(q)
    )
  }, [overviewItems, search])

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
              Dashboard
            </h1>
            <p className="mt-1 text-sm text-gray-500">Loading workspace…</p>
          </div>
        </div>
        <div className="flex min-h-[20rem] items-center justify-center rounded-shell border border-dashed border-gray-200 bg-white/60">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-8">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
          Dashboard
        </h1>
        <AppCard>
          <p className="text-center font-medium text-red-600">Failed to load dashboard data</p>
          <p className="mt-2 text-center text-sm text-gray-500">
            {error && typeof error === 'object' && 'status' in error
              ? `Error: ${String((error as { status: unknown }).status)}`
              : 'Please check your connection and try again.'}
          </p>
          <div className="mt-6 flex justify-center">
            <Button type="button" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </AppCard>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
            Dashboard
          </h1>
          <p className="mt-1 max-w-xl text-sm text-gray-500">
            High-level system statistics and quick access to common admin workflows.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-pill border-gray-300 !shadow-none"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh data
          </Button>
          <Link
            to="/admin/users"
            className="btn btn-primary inline-flex items-center justify-center gap-2 rounded-pill px-4 py-2"
          >
            <Users className="h-4 w-4" />
            Open users
          </Link>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-5 lg:gap-5">
        <AppCard className="border-gray-200/80 bg-gradient-to-br from-brand-muted/50 to-white lg:col-span-3">
          <div className="grid gap-6 sm:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Registered
              </p>
              <p className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
                {stats.registeredUsers.toLocaleString()}
              </p>
              <MiniMetricBar value={0.72} />
              <div className="mt-3 flex -space-x-2">
                {['A', 'B', 'C'].map((x, i) => (
                  <span
                    key={i}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-xs font-medium text-gray-600"
                  >
                    {x}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Today</p>
              <p className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
                {stats.usersRegisteredToday.toLocaleString()}
              </p>
              <MiniMetricBar value={todayRatio} />
              <div className="mt-3 flex -space-x-2">
                {['+'].map((x, i) => (
                  <span
                    key={i}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-emerald-100 text-xs font-semibold text-emerald-700"
                  >
                    {x}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Health</p>
              <p className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">OK</p>
              <MiniMetricBar value={1} />
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                All checks passing
              </div>
            </div>
          </div>
        </AppCard>

        <AppCard
          padding="md"
          className="flex flex-col justify-between border-gray-200/80 lg:col-span-2"
        >
          <div>
            <p className="text-sm font-medium text-gray-500">Shortcuts</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">Jump to tools</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Link
                to="/admin/users"
                className="flex flex-col items-center rounded-shell-inner border border-gray-200/90 bg-gray-50/80 px-2 py-3 text-center transition-colors hover:border-brand/40 hover:bg-brand-muted/30"
              >
                <Users className="mb-1 h-5 w-5 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Users</span>
              </Link>
              <Link
                to="/admin/notifications/push"
                className="flex flex-col items-center rounded-shell-inner border border-gray-200/90 bg-gray-50/80 px-2 py-3 text-center transition-colors hover:border-brand/40 hover:bg-brand-muted/30"
              >
                <Bell className="mb-1 h-5 w-5 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Push</span>
              </Link>
              <Link
                to="/admin/settings/password"
                className="flex flex-col items-center rounded-shell-inner border border-gray-200/90 bg-gray-50/80 px-2 py-3 text-center transition-colors hover:border-brand/40 hover:bg-brand-muted/30"
              >
                <Settings className="mb-1 h-5 w-5 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Settings</span>
              </Link>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Link
              to="/admin/cms"
              className="btn btn-primary inline-flex items-center justify-center gap-2 rounded-pill px-3 py-1.5 text-sm"
            >
              Manage content
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </AppCard>
      </div>

      <FilterBar
        leading={
          <span className="text-sm font-medium text-gray-600">
            Filters <span className="text-gray-400">·</span>{' '}
            <span className="tabular-nums text-gray-900">0</span>
          </span>
        }
        searchPlaceholder="Search metrics…"
        searchValue={search}
        onSearchChange={setSearch}
        filters={
          <>
            <div className="flex items-center sm:px-3">
              <label className="sr-only" htmlFor="dash-scope">
                Scope
              </label>
              <select
                id="dash-scope"
                className="rounded-pill border-0 bg-transparent py-2 pl-2 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/25"
                defaultValue="all"
              >
                <option value="all">All modules</option>
                <option value="users">Users</option>
                <option value="content">Content</option>
              </select>
            </div>
            <div className="flex items-center sm:px-3">
              <label className="sr-only" htmlFor="dash-range">
                Date range
              </label>
              <select
                id="dash-range"
                className="rounded-pill border-0 bg-transparent py-2 pl-2 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/25"
                defaultValue="30d"
              >
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
            </div>
          </>
        }
      />

      <AppCard padding="none" className="overflow-hidden">
        <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
          <PillTabs<DashTab>
            value={tab}
            onChange={setTab}
            options={[
              { value: 'overview', label: 'Overview' },
              { value: 'activity', label: 'Activity' },
            ]}
          />
        </div>

        {tab === 'overview' && (
          <div className="flex flex-col lg:min-h-[22rem] lg:flex-row">
            <div className="border-b border-gray-100 lg:w-[42%] lg:border-b-0 lg:border-r">
              <ul className="divide-y divide-gray-100">
                {filteredItems.map(item => {
                  const active = item.id === selectedId
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(item.id)}
                        className={`flex w-full items-center gap-3 px-5 py-4 text-left transition-colors sm:px-6 ${
                          active
                            ? 'bg-brand text-white'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                            active ? 'bg-white/20 text-white' : item.accent
                          }`}
                        >
                          {item.initials}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p
                              className={`truncate text-sm font-semibold ${
                                active ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {item.title}
                            </p>
                            {item.badge && (
                              <span
                                className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                  active
                                    ? 'bg-white/20 text-white'
                                    : 'bg-emerald-50 text-emerald-700'
                                }`}
                              >
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <p
                            className={`truncate text-xs ${
                              active ? 'text-white/80' : 'text-gray-500'
                            }`}
                          >
                            {item.subtitle}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 text-sm font-semibold tabular-nums ${
                            active ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {item.amount}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>

            <div className="flex-1 bg-gray-50/70 p-5 sm:p-6">
              {selected && (
                <div className="flex h-full flex-col rounded-shell-inner border border-gray-200/90 bg-white p-5 shadow-sm sm:p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-5">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        Detail
                      </p>
                      <h2 className="mt-1 text-xl font-semibold text-gray-900">{selected.title}</h2>
                      <p className="mt-1 text-sm text-gray-500">{selected.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                        AD
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Admin</p>
                        <p className="text-xs text-gray-500">Console</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    {[
                      {
                        label: 'Volume',
                        value:
                          selected.id === 'registered'
                            ? stats.registeredUsers.toLocaleString()
                            : selected.id === 'today'
                              ? stats.usersRegisteredToday.toLocaleString()
                              : '—',
                        hint: 'Snapshot',
                      },
                      {
                        label: 'Trend',
                        value: selected.id === 'today' ? '+24h' : 'Stable',
                        hint: 'Relative',
                      },
                      {
                        label: 'Scope',
                        value: 'Global',
                        hint: 'Tenant',
                      },
                    ].map(cell => (
                      <div
                        key={cell.label}
                        className="rounded-shell-inner border border-gray-100 bg-gray-50/80 p-4"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-gray-500">{cell.label}</p>
                          <ArrowUpRight className="h-3.5 w-3.5 text-gray-400" />
                        </div>
                        <p className="mt-2 text-lg font-semibold text-gray-900">{cell.value}</p>
                        <p className="mt-1 text-xs text-gray-400">{cell.hint}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto border-t border-gray-100 pt-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                      <div className="space-y-1 text-gray-500">
                        <div className="flex justify-between gap-8">
                          <span>Subtotal</span>
                          <span className="font-medium text-gray-900 tabular-nums">
                            {selected.amount}
                          </span>
                        </div>
                        <div className="flex justify-between gap-8">
                          <span>Last updated</span>
                          <span className="font-medium text-gray-900">
                            {new Date().toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to="/admin/users"
                          className="btn btn-secondary inline-flex items-center justify-center gap-2 rounded-pill px-3 py-1.5 text-sm"
                        >
                          <UserPlus className="h-4 w-4" />
                          Users
                        </Link>
                        <Button type="button" size="sm" className="rounded-pill" onClick={() => refetch()}>
                          Sync
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <div className="px-5 py-10 text-center sm:px-6">
            <p className="text-sm font-medium text-gray-900">Activity feed</p>
            <p className="mt-2 text-sm text-gray-500">
              Wire this tab to audit logs or notifications when the API is available.
            </p>
          </div>
        )}
      </AppCard>
    </div>
  )
}
